// PowerSync — Tutor sessions (всегда auto_subscribe) + история сообщений конкретной
// сессии (auto-stream `user_tutor_messages` — вся переписка локально).

import { useEffect, useState } from "react";
import { useAwaitingSync } from "@/hooks/use-awaiting-sync";
import { usePowerSyncQuery } from "@/lib/powersync/use-query";

// Local*-типы — РЕАЛЬНЫЕ колонки БД (information_schema). tutor_sessions.scope —
// jsonb (TEXT-строка на клиенте). tutor_messages.token_count (не tokens).
// tutor_attachments привязан к message_id (нет session_id), поля type/url/
// file_size_bytes; в реальной publication таблицы нет — данных не будет.
export interface LocalTutorSession {
	id: string;
	user_id: string;
	title: string | null;
	chat_mode: string;
	scope: string | null; // jsonb
	scenario_id: string | null;
	skill_ids: string | null; // ARRAY → JSON-строка
	messages_count: number | null;
	context_selection: string | null; // jsonb
	/** Курс, к которому привязан чат (миграция 0084). Синкается, `SELECT *` его отдаёт. */
	plan_id: string | null;
	locale: string | null;
	rolling_summary: string | null;
	last_message_at: string | null;
	created_at: string;
	updated_at: string;
	deleted_at: string | null;
}

export interface LocalTutorMessage {
	id: string;
	session_id: string;
	user_id: string;
	role: string;
	content: string;
	citations: string | null; // jsonb
	/**
	 * Структурированные content-parts (jsonb-строка). Содержит части типа
	 * `action` (rich-кнопки) и `suggestions` (follow-up chips) — восстанавливаются
	 * при перезагрузке чата (sync-rules user_tutor_messages = SELECT *).
	 */
	content_parts: string | null; // jsonb
	/**
	 * Цепочка размышлений (Course Workspace V2 §8.2) — jsonb `[{index,text}]`
	 * приезжает СТРОКОЙ. Колонка объявлена в `powersync/schema.ts`; парсер —
	 * `components/tutor/chat/persisted-thoughts.ts`.
	 */
	thoughts: string | null; // jsonb
	/** Время до первого токена ответа, мс (для строки «Размышлял N с»). */
	thinking_ms: number | null;
	token_count: number | null;
	/** finish_reason из Postgres — нужен для гейта follow-up chips. */
	finish_reason: string | null;
	error_message: string | null;
	created_at: string;
}

export interface LocalTutorAttachment {
	id: string;
	message_id: string;
	user_id: string;
	type: string;
	url: string;
	mime_type: string | null;
	file_size_bytes: number | null;
	filename: string | null;
	created_at: string;
}

/** Потолок ожидания waitForStream — иначе offline/зависший sync = вечный skeleton. */
const STREAM_WAIT_CEILING_MS = 12_000;

/** Сессия + локальный preview последнего сообщения (без server JOIN). */
export interface LocalTutorSessionWithPreview extends LocalTutorSession {
	preview: string | null;
}

export function useTutorSessionsQuery(userId: string | undefined) {
	return usePowerSyncQuery<LocalTutorSessionWithPreview>(
		`SELECT s.*,
		        (SELECT substr(m.content, 1, 120)
		           FROM tutor_messages m
		          WHERE m.session_id = s.id
		          ORDER BY m.created_at DESC
		          LIMIT 1) AS preview
		   FROM tutor_sessions s
		  WHERE s.user_id = ? AND s.deleted_at IS NULL
		ORDER BY s.updated_at DESC`,
		[userId ?? ""],
	);
}

/**
 * История tutor-сессии из локальной SQLite.
 *
 * Вся переписка реплицируется автоматически (stream `user_tutor_messages`,
 * `auto_subscribe: true`), поэтому подписываться на конкретную сессию не нужно.
 * Раньше здесь была on-demand подписка с TTL 1800s: чат, открытый более
 * получаса назад, офлайн оказывался пустым.
 *
 * Остаётся ожидание появления самой сессии в SQLite — read-after-write лаг
 * между tRPC create и синком (`useAwaitingSync`). `STREAM_WAIT_CEILING_MS`
 * снимает вечный skeleton при зависшем синке.
 */
export function useTutorSessionMessagesQuery(sessionId: string | undefined) {
	const sessionLookup = usePowerSyncQuery<{ id: string }>(
		`SELECT id FROM tutor_sessions
		   WHERE id = ? AND deleted_at IS NULL
		   LIMIT 1`,
		[sessionId ?? ""],
	);
	const sessionPresent = Boolean(sessionId && sessionLookup.data[0]?.id);
	// present=true когда нет sessionId (хук idle) ИЛИ сессия уже в SQLite.
	const awaitingSession = useAwaitingSync(Boolean(!sessionId || sessionPresent));

	// Тай-брейк по `id` обязателен: у части ходов вопрос и ответ имеют РОВНО
	// один `created_at` (инфографика ставит обе строки одним временем — на
	// 2026-08-13 таких пар в базе 10). Без второго ключа порядок при совпадении
	// не определён, и лента могла показать ответ выше вопроса, а `messages.at(-1)`
	// — отдать `user` вместо `assistant`, из-за чего история открывалась в
	// режиме активного хода (вопрос прибит к верхней кромке). `id` — UUID v7,
	// монотонный внутри миллисекунды, поэтому даёт ровно порядок вставки.
	const query = usePowerSyncQuery<LocalTutorMessage>(
		`SELECT * FROM tutor_messages
		   WHERE session_id = ?
		ORDER BY created_at ASC, id ASC`,
		[sessionId ?? ""],
	);

	// Первичный синк ещё идёт: сессия уже приехала, сообщения — нет.
	const waitingOnFirstSync = Boolean(sessionId && sessionPresent && query.isLoading);
	const [streamWaitExpired, setStreamWaitExpired] = useState(false);
	useEffect(() => {
		if (!waitingOnFirstSync) {
			setStreamWaitExpired(false);
			return;
		}
		const t = window.setTimeout(() => setStreamWaitExpired(true), STREAM_WAIT_CEILING_MS);
		return () => window.clearTimeout(t);
	}, [waitingOnFirstSync, sessionId]);

	const streamPending = waitingOnFirstSync && !streamWaitExpired;
	const stillAwaiting = Boolean(sessionId) && (awaitingSession || streamPending);

	return {
		data: query.data,
		isLoading: stillAwaiting,
		isFetching: query.isFetching,
		error: query.error,
		refresh: query.refresh,
		/**
		 * PowerSync «закончил попытку» отдать историю: first sync, expiry ожидания
		 * сессии, или потолок waitForStream. Нужен потребителям tRPC-fallback.
		 */
		streamReady: !sessionId || !stillAwaiting,
	};
}

/**
 * Находит id tutor-сессии, привязанной к конкретному аудио-резюме (recap chat
 * под капотом — `tutor_session` с `chat_mode='materials'` и
 * `context_selection.audioRecapId`). `tutor_sessions` авто-синкается, поэтому
 * читаем локально через json_extract (PowerSync SQLite поддерживает JSON1).
 *
 * Возвращает `null` пока сессия не создана (recap, по которому ещё не писали).
 */
export function useRecapChatSessionId(recapId: string | undefined): {
	sessionId: string | null;
	isLoading: boolean;
} {
	const { data, isLoading } = usePowerSyncQuery<{ id: string }>(
		`SELECT id FROM tutor_sessions
		   WHERE chat_mode = 'materials'
		     AND json_extract(context_selection, '$.audioRecapId') = ?
		     AND deleted_at IS NULL
		   LIMIT 1`,
		[recapId ?? ""],
	);
	return { sessionId: data[0]?.id ?? null, isLoading };
}

export function useTutorSessionAttachmentsQuery(sessionId: string | undefined) {
	// tutor_attachments привязан к message_id (нет session_id) — JOIN на
	// tutor_messages. Оба набора реплицируются автоматически стримом
	// `user_tutor_messages`, поэтому явная подписка не нужна.
	return usePowerSyncQuery<LocalTutorAttachment>(
		`SELECT a.* FROM tutor_attachments a
		   JOIN tutor_messages m ON m.id = a.message_id
		  WHERE m.session_id = ?
		ORDER BY a.created_at ASC`,
		[sessionId ?? ""],
	);
}
